import { useFlags } from 'launchdarkly-react-client-sdk';
import { cloneDeep, isEqual, isObject } from 'lodash';
import React, { createContext, useContext, useEffect, useState } from 'react';
import ReactModal from 'react-modal';
import { toastSuccess, toastError } from '../utils/toast';
import {
  TitleBar,
  ItemCont,
  Row,
  ControlsCont,
  Block,
  RawEdit,
  TextInput,
  CloseButton,
  ContentCont,
  PrimaryButton,
  TextButton,
  GhostButton,
  RawEditToggle,
  ModalContents,
} from './styles';
import lightCloseButton from './icons/lightCloseButton.svg';
import Switch from '../elements/Switch';
import styled from 'styled-components';
import * as environmentUtils from '../helpers/environment';
import update from 'immutability-helper';
import { connect } from 'react-redux';
import { setShowPLV2, setIsEndorsementsRefactorEnabled } from '../actions/application';

export const FeatureFlagContext = createContext({});

const UpdateFlag = ({ flags, flag, handleUpdate }: any) => {
  const [value, setValue] = useState(flags[flag]);

  const handleCheckboxOnChange = () => {
    setValue((value: boolean) => !value);
    handleUpdate(flag, !value);
    // TODO: Uncomment and implement once we have upgraded to react 18
    toastSuccess('Success', `The ${flag} flag has been turned ${value ? 'off' : 'on'}`);
  };

  const handleTextUpdate = (flag: string, value: string) => {
    handleUpdate(flag, value);
    // TODO: Uncomment and implement once we have upgraded to react 18
    toastSuccess('Success', `The ${flag} flag has been changed to ${value}`);
  };

  const handleTextOnChange = (flag: string, value: string) => {
    // debounce((flag, value) => {
    handleTextUpdate(flag, value);
    // }, 500);
  };

  switch (typeof flags[flag]) {
    case 'boolean':
      return (
        <div style={{ display: 'flex' }}>
          <p style={{ margin: 'auto', marginRight: '10px' }}>Off</p>
          <Switch
            ariaLabel={`change ${flag} to ${!value}`}
            id={`${flag}-checkbox`}
            checked={value}
            onChange={handleCheckboxOnChange}
          />
          <p style={{ margin: 'auto', marginLeft: '10px' }}>On</p>
        </div>
      );
    case 'string':
      return (
        <TextInput
          aria-label={`change ${flag} value`}
          type="text"
          placeholder={value}
          // onChange={(e: any) => }
          onBlur={(e: any) => handleTextOnChange(flag, e.target.value)}
        />
      );
    default:
      return <span>Use raw edit</span>;
  }
};

const SHORTCUT_NOTICE_KEY = 'fftools-shortcut-notice-dismissed';

const FeatureFlagTools = ({ flags, flagOverrides, setFlagOverrides }: any) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showNotice, setShowNotice] = useState(
    () => !environmentUtils.isProd() && !environmentUtils.isSandbox() && !localStorage.getItem(SHORTCUT_NOTICE_KEY)
  );

  const handleDismissNotice = () => {
    localStorage.setItem(SHORTCUT_NOTICE_KEY, 'true');
    setShowNotice(false);
  };
  const [rawFlags, setRawFlags] = useState(JSON.stringify({ ...flags, ...flagOverrides }, undefined, 2));
  const [rawEditError, setRawEditError] = useState('');
  const [showRawEdit, setShowRawEdit] = useState(false);
  const [, setStoredOverrides] = useLocalStorage('overrides', {});

  const flagList = Object.keys(flags).filter((flag) => flag !== 'enableFeatureFlagTools');
  useEffect(() => {
    setRawFlags(JSON.stringify({ ...flags, ...flagOverrides }, undefined, 2));
  }, [flagOverrides, flags]);

  const handleClose = () => setIsOpen(false);

  useEffect(() => {
    if (environmentUtils.isProd() || environmentUtils.isSandbox()) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey && e.shiftKey && e.code === 'KeyF') {
        setIsOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleUpdate = (flag: string, value: any) => {
    setFlagOverrides((p: any) => {
      p = update(p, {
        [flag]: { $set: value },
      });
      return p;
    });
    setStoredOverrides((p: any) => {
      p = update(p, {
        [flag]: { $set: value },
      });
      return p;
    });
  };

  const handleRawEditOnChange = (e: any) => {
    setRawFlags(e.target.value);
  };

  const handleSaveRawEdit = () => {
    try {
      const newValue = JSON.parse(rawFlags);
      if (Object.keys(newValue).length === Object.keys(flags).length) {
        setFlagOverrides(newValue);
        setStoredOverrides(newValue);
        setRawEditError('');
        // TODO: Uncomment and implement once we have upgraded to react 18
        toastSuccess('Success', 'Raw edit is saved.');
      } else {
        setRawEditError('Cannot add or remove flags in dev tools');
        // TODO: Uncomment and implement once we have upgraded to react 18
        toastError('Error', 'Cannot add or remove flags in dev tools');
      }
    } catch (e) {
      setRawEditError('Must be valid JSON');
      // TODO: Uncomment and implement once we have upgraded to react 18
      toastError('Error', 'Must be valid JSON');
    }
  };

  const handleReset = (flag: string) => {
    const resetFn = (flagOverrides: any) => {
      const flagOverridesCopy = cloneDeep(flagOverrides);
      delete flagOverridesCopy[flag];

      return flagOverridesCopy;
    };
    setFlagOverrides(resetFn);
    setStoredOverrides(resetFn);
    // TODO: Uncomment and implement once we have upgraded to react 18
    toastSuccess('Success', `The ${flag} flag has been reset to its original status`);
  };

  const handleResetAll = () => {
    setFlagOverrides({});
    setStoredOverrides({});
    setRawEditError('');
    toastSuccess('Success', 'All Feature Flags have been reset to their original status');
  };

  return (
    <>
      {showNotice && (
        <ShortcutNotice>
          <span>
            Feature Flag Dev Tools is now accessible via keyboard shortcut: <strong>⌘+Shift+F</strong>
          </span>
          <NoticeDismiss aria-label="dismiss notice" onClick={handleDismissNotice}>
            ✕
          </NoticeDismiss>
        </ShortcutNotice>
      )}
      <ReactModal
        isOpen={isOpen}
        onRequestClose={handleClose}
        style={{
          overlay: {
            zIndex: 9999,
          },
          content: {
            position: 'absolute',
            top: '5%',
            left: '20%',
            right: '20%',
            bottom: '20%',
            border: '1px solid #ccc',
            background: '#fff',
            overflow: 'scroll',
            WebkitOverflowScrolling: 'touch',
            borderRadius: '4px',
            outline: 'none',
            padding: '0',
            zIndex: 9999,
          },
        }}
      >
        <ModalContents>
          <div>
            <CloseButton aria-label="close dev tools" onClick={handleClose}>
              <img alt="close icon" src={lightCloseButton} />
            </CloseButton>
            <TitleBar>Feature Flag Dev Tools</TitleBar>
          </div>
          <ContentCont style={{ height: 'auto' }}>
            <Row>
              <Block>
                <h3>Flags</h3>
              </Block>
              <Block>
                <h3>Values</h3>
              </Block>
              <Block>
                <h3>Overrides</h3>
              </Block>
              <Block>
                <h3>Update</h3>
              </Block>
              <Block halfWidth={true}>
                <h3>Reset</h3>
              </Block>
            </Row>
            <ItemCont>
              {flagList.map((flag) => {
                const isFlagValueObj = isObject(flags[flag]) && !Array.isArray(flags[flag]);
                const areObjectsEqual = isEqual(flags[flag], flagOverrides[flag]);
                const flagValueStringified = flags[flag]?.toString();
                const objectOverrideMsg =
                  areObjectsEqual || !Boolean(flagOverrides[flag]) ? 'no override' : 'Obj is overridden';
                const overrideValueStringified = isFlagValueObj ? objectOverrideMsg : flagOverrides[flag]?.toString();
                const flagValue = isFlagValueObj ? 'Object' : flagValueStringified;
                const hasOverride =
                  (flagOverrides[flag] != null && flags[flag].toString() !== flagOverrides[flag].toString()) ||
                  (isFlagValueObj && !areObjectsEqual);
                const overrideValue = hasOverride ? overrideValueStringified : 'no override';

                return (
                  <Row key={`${flag}_${flagValue}_${overrideValue}`}>
                    <Block>{flag}</Block>
                    <Block>{flagValue}</Block>
                    <Block>{overrideValue}</Block>
                    <Block style={{ display: 'flex', alignContent: 'center', flexWrap: 'wrap' }}>
                      <UpdateFlag flags={{ ...flags, ...flagOverrides }} flag={flag} handleUpdate={handleUpdate} />
                    </Block>
                    <Block halfWidth={true}>
                      <TextButton aria-label={`reset ${flag} flag`} onClick={() => handleReset(flag)}>
                        Reset
                      </TextButton>
                    </Block>
                  </Row>
                );
              })}
            </ItemCont>
            <RawEditToggle
              data-testid="open_feature_flag_dev_tools_raw_edit"
              onClick={() => setShowRawEdit(!showRawEdit)}
            >
              Toggle Raw Edit
            </RawEditToggle>
            {showRawEdit && (
              <div>
                <h2>Raw Edit</h2>
                <RawEdit
                  aria-label={'edit raw data'}
                  value={rawFlags}
                  onChange={handleRawEditOnChange}
                  hasError={Boolean(rawEditError)}
                />
                {rawEditError && <ErrorText style={{ color: 'red' }}>{rawEditError}</ErrorText>}
                <PrimaryButton onClick={handleSaveRawEdit}>Save Raw Edits</PrimaryButton>
              </div>
            )}
          </ContentCont>
          <ControlsCont>
            <GhostButton onClick={handleResetAll}>Reset All</GhostButton>
          </ControlsCont>
        </ModalContents>
      </ReactModal>
    </>
  );
};

/**
 * useFeatureFlags is a custom hook which returns all feature flags with local overrides (using Feature Flag Dev Tools in lower environments).
 * It uses the useContext primitive to access the LaunchDarkly context set up by withLDProvider.
 * As such you will still need to use the withLDProvider HOC at the root of your app to initialize the React SDK and populate the context with ldClient and your flags.
 * @return — All the feature flags configured in your LaunchDarkly project with flag overrides
 */
export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagContext);

  if (!context) {
    throw new Error('useFeatureFlag hook must be rendered within the FeatureFlagProvider');
  }

  return context as any;
};

const useLocalStorage = (storageKey: string, fallbackState: any) => {
  const prevStoredVal = localStorage.getItem(storageKey);
  const [value, setValue] = useState(prevStoredVal ? JSON.parse(prevStoredVal) : fallbackState);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(value));
  }, [value, storageKey]);

  return [value, setValue];
};

interface FeatureFlagProviderProps {
  children?: React.ReactNode;
  setShowPLV2?: (value: boolean) => void;
  setIsEndorsementsRefactorEnabled?: (value: boolean) => void;
}

export const FeatureFlagProviderComponent = ({
  setShowPLV2,
  setIsEndorsementsRefactorEnabled,
  ...props
}: FeatureFlagProviderProps) => {
  const flags = useFlags();
  const [storedOverrides] = useLocalStorage('overrides', {});
  const [flagOverrides, setFlagOverrides] = useState(storedOverrides);

  const value = {
    ...flags,
    ...flagOverrides,
  };

  useEffect(() => {
    if (setShowPLV2) setShowPLV2(flags.showPlv2);
  }, [flags.showPlv2, setShowPLV2]);

  useEffect(() => {
    if (setIsEndorsementsRefactorEnabled) setIsEndorsementsRefactorEnabled(flags.isQuoteFlowImprovementsEnabled);
  }, [flags.isQuoteFlowImprovementsEnabled, setIsEndorsementsRefactorEnabled]);

  return (
    <FeatureFlagContext.Provider value={value}>
      {props.children}
      <FeatureFlagTools flags={flags} flagOverrides={flagOverrides} setFlagOverrides={setFlagOverrides} />
    </FeatureFlagContext.Provider>
  );
};

export const FeatureFlagProvider = connect(null, { setShowPLV2, setIsEndorsementsRefactorEnabled })(
  FeatureFlagProviderComponent
);

const ErrorText = styled.p`
  color: red;
`;

const ShortcutNotice = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  background: #0d0d46;
  color: #fff;
  font-size: 14px;
`;

const NoticeDismiss = styled.button`
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  font-size: 16px;
  padding: 0 0 0 16px;
  line-height: 1;
`;

// Non-React Utility Functions
// These functions allow feature flag access outside of React components
// They use the same data sources as FeatureFlagProvider (localStorage overrides + LaunchDarkly)

/**
 * Gets a feature flag value outside of React components
 * Uses the same logic as FeatureFlagProvider: localStorage overrides take precedence over LaunchDarkly
 * @param {string} flagName - The name of the feature flag
 * @param {any} defaultValue - Default value if flag is not found
 * @returns {any} The feature flag value
 */
export const getFeatureFlag = (flagName: string, defaultValue: any = false): any => {
  try {
    // First check localStorage overrides (same as FeatureFlagProvider)
    const storedOverrides = localStorage.getItem('overrides');
    if (storedOverrides) {
      const overrides = JSON.parse(storedOverrides);
      if (overrides.hasOwnProperty(flagName)) {
        return overrides[flagName];
      }
    }

    // Then check LaunchDarkly client if available (same as useFlags() in FeatureFlagProvider)
    if (typeof window !== 'undefined' && (window as any).ldClient) {
      const ldValue = (window as any).ldClient.variation(flagName, defaultValue);
      if (ldValue !== undefined) {
        return ldValue;
      }
    }

    // Fallback to default value
    return defaultValue;
  } catch (error) {
    console.warn(`Error getting feature flag "${flagName}":`, error);
    return defaultValue;
  }
};

/**
 * Checks if a feature flag is enabled (for boolean flags)
 * @param {string} flagName - The name of the feature flag
 * @param {boolean} defaultValue - Default value if flag is not found
 * @returns {boolean} Whether the flag is enabled
 */
export const isFeatureFlagEnabled = (flagName: string, defaultValue: boolean = false): boolean => {
  const value = getFeatureFlag(flagName, defaultValue);
  return Boolean(value);
};
